import { PayloadAction } from '@reduxjs/toolkit';
import { AddContactInfoRequest } from '@/types/entities/contactInfo';
import { AddAddressRequest } from '@/domains/addresses/types/api/requests/AddAddressRequest';
import { RootState } from '@/store/types/RootState';
import { DeleteInsuredRequest } from '../types/api/requests/DeleteInsuredRequest';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getInsuredDetails } from '@/features/entityDetails/store/entityDetailsSlice';
import { resetGeographyState } from '@/features/geography/store/geographySlice';
import { resetAddressValidationState } from '@/features/googleApi/addressValidationApi/store/googleAddressValidationApiSlice';
import { resetGooglePlacesState } from '@/features/googleApi/placesApi/store/googlePlacesApiSlice';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { getClaimSetV2 } from '@/domains/claimSets/store/claimSetSlice';
import {
  addInsuredAddress,
  addInsuredContactInfo,
  deleteInsured,
  deleteInsuredSuccess,
} from '@/domains/insureds/store/insuredSlice';
import { FAILED_TO_SAVE } from '@/constants/texts';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* addInsuredAddressSaga({ payload }: PayloadAction<AddAddressRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/insureds/add-address`, payload);
    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(getInsuredDetails({ insuredId: `${payload.entityId}` }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetGeographyState());
    yield put(resetAddressValidationState());
    yield put(resetGooglePlacesState());
    ModalService.close(Modals.AddAddress);
  }
}

function* addInsuredContactInfoSaga({ payload }: PayloadAction<AddContactInfoRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/insureds/add-contact-information`, payload);
    yield put(getInsuredDetails({ insuredId: `${payload.entityId}` }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.AddContactInfo);
  }
}

function* deleteInsuredSaga({ payload: { insuredId, closeInsuredDrawer } }: PayloadAction<DeleteInsuredRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));

    const currentClaimSetId = yield select((state: RootState) => state.claimSets.currentClaimSetId);

    yield call(HttpService.delete, `/examiner/insureds/${insuredId}`);

    closeInsuredDrawer();

    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(deleteInsuredSuccess());
    yield put(getClaimSetV2(`${currentClaimSetId}`));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: FAILED_TO_SAVE,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    ModalService.close(Modals.Confirmation);
    yield put(setSystemLoadingStatus(false));
  }
}

export function* insuredWatcher() {
  yield takeLatest(addInsuredAddress.type, addInsuredAddressSaga);
  yield takeLatest(addInsuredContactInfo.type, addInsuredContactInfoSaga);
  yield takeLatest(deleteInsured.type, deleteInsuredSaga);
}
